import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Input, message, Modal, Popconfirm } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import OWButton from '../ow_button/OWButton';
import { AssetSuggestion } from '../../types/InvoiceDecisionTypes';
import { nullSafeGet, nullSafeGetOrElse, nullSafeGetWithZero } from '../../utils/DataAccessUtils';
import {
	ASSET_FIELD_LABELS,
	getSuggestedAssetFieldUpdates,
	isSuggestedAssetActive,
} from '../../utils/InvoiceUtils';

interface EnrichAssetModalProps {
	visible: boolean;
	assetSuggestion: AssetSuggestion;
	asset: any;
	applying?: boolean;
	onEnrich: (updates: any) => Promise<any> | void;
	onCancel: () => void;
	/** Show the link step first; the fields follow once the parent reports the link done. */
	linkPending?: boolean;
	/** What the work order links now, for the replace warning. Either may be missing. */
	linkedAsset?: any;
	linkedAssetId?: number;
	/** Whether a fields step will follow the link, so the link step can say so. */
	canEnrich?: boolean;
	linking?: boolean;
	onLink?: () => Promise<any> | void;
}

/**
 * Review and apply what the invoice printed about the work order's asset.
 *
 * Every value is editable and every row is opt-in: the backend only ever suggests, and a value it
 * read off a PDF is a starting point for a person, not an answer. Rows whose value the asset
 * record already carries are dropped — once applied, there is nothing left to review, which is
 * also what stops the banner reappearing after a successful enrich.
 *
 * For a link suggestion the modal opens on a link step instead — which asset, whether it replaces
 * the current one, whether it is inactive — and moves to the fields once the link succeeds. That
 * step IS the confirmation (its title asks the question), so its button writes directly rather
 * than through a Popconfirm; OWButton plus `linking` guards the double click.
 */

/**
 * The link step's wording, shared by the banner and the modal so the two cannot drift. The banner
 * and modal titles differ only in case, as designed.
 */
export const getAssetLinkCopy = (replacesLinkedAsset: boolean) =>
	replacesLinkedAsset
		? {
				bannerTitle: 'Replace Asset?',
				modalTitle: 'Replace asset?',
				subtitle:
					'This work order is currently linked to another asset, but we found a different match on this invoice.',
		  }
		: {
				bannerTitle: 'Link Asset?',
				modalTitle: 'Link Asset?',
				subtitle:
					'This work order doesn\u2019t have an asset yet, but we detected a match from the invoice',
		  };

/** A link replaces the current one when the work order links some OTHER asset. */
export const replacesLinkedAssetFor = (linkedAssetId: any, suggestedAssetId: any) =>
	!!linkedAssetId && `${linkedAssetId}` !== `${suggestedAssetId}`;

interface AssetSummaryProps {
	name: string;
	assetTypeName?: string;
	assetId: any;
	isActive?: boolean;
}

/** Name over "Type • Asset ID n", with an Inactive marker — the one shape every asset here takes. */
const AssetSummary: React.FC<AssetSummaryProps> = ({ name, assetTypeName, assetId, isActive }) => (
	<>
		<div className="bodyregular text-character-primary">{name}</div>
		<div className="footnoteregular flex flex-row flex-wrap gap-x-2xs text-character-secondary">
			{assetTypeName ? <span>{assetTypeName}</span> : null}
			{assetTypeName ? <span>•</span> : null}
			<span>Asset ID {assetId}</span>
			{isActive === false ? <span>•</span> : null}
			{isActive === false ? <span className="text-character-danger">Inactive</span> : null}
		</div>
	</>
);
export const EnrichAssetModal: React.FC<EnrichAssetModalProps> = ({
	visible,
	assetSuggestion,
	asset,
	applying,
	onEnrich,
	onCancel,
	linkPending,
	linkedAsset,
	linkedAssetId,
	canEnrich,
	linking,
	onLink,
}) => {
	const fields = useMemo(
		() => getSuggestedAssetFieldUpdates(assetSuggestion, asset),
		[assetSuggestion, asset]
	);

	// Keyed by field so a re-render cannot reorder them. Every row starts UNSELECTED: since the
	// backend stopped gating suggestions on confidence, a field arrives with its probability
	// attached and no floor under it — a blank field used to need >0.9 and now comes through at any
	// value. Pre-checking would turn a low-probability guess into the default, so each row is an
	// explicit choice and ENRICH stays disabled until one is made.
	const [selectedByField, setSelected] = useState<any>({});
	const [edited, setEdited] = useState<any>({});

	const valueFor = useCallback(
		(field) => (edited[field.field] !== undefined ? edited[field.field] : field.invoiceValue),
		[edited]
	);

	const checkedFields = useMemo(
		() => fields.filter((field) => selectedByField[field.field]),
		[fields, selectedByField]
	);

	const overwritesExisting = useMemo(
		() => checkedFields.some((field) => !!field.currentValue),
		[checkedFields]
	);

	const handleToggle = useCallback(
		(field) => (event) => {
			setSelected({ ...selectedByField, [field.field]: event.target.checked });
		},
		[selectedByField]
	);

	const handleChange = useCallback(
		(field) => (event) => {
			setEdited((previous) => ({ ...previous, [field.field]: event.target.value }));
		},
		[]
	);

	const handleEnrich = useCallback(() => {
		const updates = {};
		checkedFields.forEach((field) => {
			const value = `${valueFor(field)}`.trim();
			if (value === '') {
				return;
			}
			updates[field.assetField] = value;
		});
		if (Object.keys(updates).length === 0) {
			message.error('Nothing to enrich — every selected field is empty.');
			return Promise.resolve();
		}
		// Handed back to the Popconfirm: a returned promise makes its confirm button show a spinner
		// and hold the popover open until the write settles.
		return onEnrich(updates);
	}, [checkedFields, valueFor, onEnrich]);

	const assetTypeName =
		nullSafeGet('assetType.name', asset) || nullSafeGetOrElse('assetTypeName', asset, '');
	const assetId = nullSafeGet('assetId', assetSuggestion);
	// The live record wins once loaded: someone may have reactivated the asset since the parse.
	const isAssetActive = asset
		? nullSafeGetWithZero('isActive', asset) !== false
		: isSuggestedAssetActive(assetSuggestion);
	const replacesLinkedAsset = replacesLinkedAssetFor(linkedAssetId, assetId);
	// The work order's asset is only trusted when it is hydrated AND is the one the work order links.
	const linkedAssetRecord =
		nullSafeGet('id', linkedAsset) && `${nullSafeGet('id', linkedAsset)}` === `${linkedAssetId}`
			? linkedAsset
			: null;
	const assetName = nullSafeGetOrElse('name', asset, `Asset ${assetId}`);

	const handleLink = () => {
		onLink();
	};

	const linkFooter = (
		<div className="flex flex-row items-center justify-end gap-x-s">
			<Button onClick={onCancel}>SKIP</Button>
			<OWButton type="primary" loading={linking} onClick={handleLink}>
				{replacesLinkedAsset ? 'REPLACE ASSET' : 'LINK ASSET'}
			</OWButton>
		</div>
	);

	const linkNotes = (
		<>
			{isAssetActive ? null : (
				<div className="bodyregular text-character-primary">
					This asset is inactive. You can still link it; its status will not change.
				</div>
			)}
			{canEnrich && fields.length > 0 ? (
				<div className="footnoteregular text-character-secondary">
					After linking, you can review {fields.length} suggested{' '}
					{fields.length === 1 ? 'update' : 'updates'} to this asset.
				</div>
			) : null}
		</>
	);
	const hasLinkNotes = !isAssetActive || (canEnrich && fields.length > 0);

	const linkCopy = getAssetLinkCopy(replacesLinkedAsset);
	const modalHeading = linkPending
		? { title: linkCopy.modalTitle, subtitle: linkCopy.subtitle }
		: {
				title: 'Enrich Data?',
				subtitle: 'We found invoice details that can help update the fields below. Please review.',
		  };
	const showsReplaceWarning = linkPending && replacesLinkedAsset;

	return (
		<Modal
			visible={visible}
			width={showsReplaceWarning ? 720 : 572}
			onCancel={onCancel}
			bodyStyle={{ padding: 0 }}
			title={
				<div className="flex flex-row items-start gap-x-s">
					{showsReplaceWarning ? (
						// `!`: antd's `.anticon { color: inherit }` loads after Tailwind and ties on
						// specificity, so a plain text-* class loses and the icon renders black.
						<ExclamationCircleFilled className="pt-2xs text-2xl !text-character-danger" />
					) : null}
					<div className="flex flex-col gap-y-2xs">
						<div className="h5medium text-character-primary">{modalHeading.title}</div>
						<div
							className={
								showsReplaceWarning
									? 'bodyregular text-character-primary'
									: 'bodyregular text-character-secondary'
							}
						>
							{modalHeading.subtitle}
						</div>
					</div>
				</div>
			}
			footer={
				linkPending ? (
					linkFooter
				) : (
					<div className="flex flex-row items-center justify-end gap-x-s">
						<Button onClick={onCancel}>SKIP</Button>
						<Popconfirm
							// Always confirm: this writes to a record the invoice is only evidence about.
							title={
								<div className="flex max-w-[320px] flex-col gap-y-2">
									<div className="h5medium text-character-primary">
										{overwritesExisting ? 'Overwrite existing data?' : 'Enrich asset record?'}
									</div>
									<div className="bodyregular text-character-primary">
										{overwritesExisting
											? 'Enriching these fields will overwrite existing data.'
											: 'These values will be written to the asset record.'}
									</div>
								</div>
							}
							okText="Confirm"
							cancelText="Cancel"
							onConfirm={handleEnrich}
							disabled={checkedFields.length === 0}
						>
							<Button type="primary" loading={applying} disabled={checkedFields.length === 0}>
								ENRICH ({checkedFields.length})
							</Button>
						</Popconfirm>
					</div>
				)
			}
		>
			{showsReplaceWarning ? (
				<div className="flex flex-col gap-y-xs p-l">
					<div className="grid grid-cols-2 border border-solid border-grey-4">
						<div className="bodyregular border-0 border-b border-solid border-grey-4 bg-grey-2 px-m py-s text-character-secondary">
							Work Order (Currently Linked)
						</div>
						<div className="bodyregular border-0 border-b border-solid border-grey-4 bg-grey-2 px-m py-s text-character-secondary">
							Uploaded Invoice
						</div>
						<div className="flex flex-col px-m py-m">
							<AssetSummary
								name={nullSafeGetOrElse('name', linkedAssetRecord, `Asset ${linkedAssetId}`)}
								assetTypeName={
									nullSafeGet('assetType.name', linkedAssetRecord) ||
									nullSafeGet('assetTypeName', linkedAssetRecord)
								}
								assetId={linkedAssetId}
								isActive={
									linkedAssetRecord
										? nullSafeGetWithZero('isActive', linkedAssetRecord) !== false
										: undefined
								}
							/>
						</div>
						<div className="flex flex-col px-m py-m">
							<AssetSummary
								name={assetName}
								assetTypeName={assetTypeName}
								assetId={assetId}
								isActive={isAssetActive}
							/>
						</div>
					</div>
					{linkNotes}
				</div>
			) : (
				<div className="border-0 border-b border-solid border-grey-4 bg-grey-3 px-l py-xs">
					<AssetSummary
						name={assetName}
						assetTypeName={assetTypeName}
						assetId={assetId}
						isActive={isAssetActive}
					/>
				</div>
			)}
			{linkPending ? (
				!showsReplaceWarning && hasLinkNotes ? (
					<div className="flex flex-col gap-y-xs p-l">{linkNotes}</div>
				) : null
			) : (
				<div className="flex flex-col p-l">
					{fields.map((field) => {
						const isChecked = !!selectedByField[field.field];
						return (
							<div className="flex flex-row items-start gap-x-s pb-l" key={field.field}>
								<div className="flex h-6 items-center">
									<Checkbox checked={isChecked} onChange={handleToggle(field)} />
								</div>
								<div className="flex flex-1 flex-col">
									<div className="bodyregular pb-2 text-character-primary">
										{ASSET_FIELD_LABELS[field.field] || field.field}:
									</div>
									<Input
										value={valueFor(field)}
										disabled={!isChecked}
										onChange={handleChange(field)}
									/>
									{/* Only meaningful when the record already holds something this would replace. */}
									{field.currentValue ? (
										<div className="footnoteregular pt-1 text-character-secondary">
											{field.currentValue} → {valueFor(field)}
										</div>
									) : null}
								</div>
							</div>
						);
					})}
				</div>
			)}
		</Modal>
	);
};

export default EnrichAssetModal;
